import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import {
  usePortfolioLinkSettings,
  usePortfolioLinkSettingsMutation,
} from '../../hooks/usePortfolioLinkSettings';

import SyncedState from './SyncedState';
import DisconnectedState from './DisconnectedState';
import ResyncingState from './ResyncingState';
import { YodleeDisconnectedState } from './YodleeDisconnectedState';
import { useFlag } from '@simplywallst/unleash';

interface Props {
  portfolio: Portfolio;
}

const PortfolioLinkStatus = ({ portfolio }: Props) => {
  const flag = useFlag('mono-portfolio-linking-limits');
  const isLimitedLinkedPortfolioEnabled = flag.enabled && flag.ready;

  const { data: portfolioLinkSettings, status: settingsStatus } =
    usePortfolioLinkSettings({ portfolioId: portfolio.id });
  const { mutate: updatePortfolioLinkSettings } =
    usePortfolioLinkSettingsMutation({ portfolioId: portfolio.id });

  if (settingsStatus !== 'success') {
    return null;
  }

  if (portfolio.link.status === 'synced') {
    return (
      <SyncedState
        portfolio={portfolio}
        linkSettings={portfolioLinkSettings}
        updateLinkSettings={updatePortfolioLinkSettings}
      />
    );
  }

  if (
    portfolio.provider.name === 'yodlee' &&
    portfolio.link.status === 'disconnected'
  ) {
    return <YodleeDisconnectedState />;
  }

  if (
    !isLimitedLinkedPortfolioEnabled &&
    portfolio.link.status === 'disconnected'
  ) {
    return <DisconnectedState portfolio={portfolio} />;
  }

  if (portfolio.link.status === 'resyncing') {
    return <ResyncingState />;
  }

  return null;
};

export default PortfolioLinkStatus;
